// src/components/Layout.js

import React, { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { Box, CssBaseline, Typography, Button } from '@mui/material';
import { Outlet } from 'react-router-dom';
import CreatorSidebar, { collapsedWidth } from './CreatorSidebar';
import CustomerSidebar from './CustomerSidebar';
import StudentSidebar from './StudentSidebar';
import TradingSidebar from './TradingSidebar';
import FinanceSidebar from './FinanceSidebar';
import Footer from './Footer';
import Header from './Header';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { SETUP_COMPLETE_LOCAL_KEY } from "../config";
import { PanelProvider, usePanel, PanelType } from '../contexts/PanelContext';
import { FinanceAccountProvider } from '../contexts/FinanceAccountContext';
import { CompanySettingsProvider } from '../contexts/CompanySettingsContext';

const drawerWidth = 240;

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

// Persistent banner shown across the dashboard while a subscription is in its
// recurring 15-day grace window (a paid period or trial ended; the next charge has
// not been attempted yet). The user keeps full access until the grace window closes.
function GraceBanner({ info, onManage }) {
    const formatDate = (iso) => {
        if (!iso) return '';
        try {
            return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        } catch (e) {
            return '';
        }
    };
    const graceEnd = formatDate(info?.grace_period_ends_at);
    const canResubscribe = !!info?.can_resubscribe;
    return (
        <Box sx={{
            mb: 2, p: 1.5, borderRadius: '12px',
            background: 'linear-gradient(135deg,#fef3c7,#fde68a)',
            border: '1px solid #f59e0b',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 1, fontFamily,
        }}>
            <Typography sx={{ fontFamily, fontSize: '0.875rem', color: '#92400e', fontWeight: 500 }}>
                Your subscription period has ended.{' '}
                {canResubscribe
                    ? `We'll attempt to charge your default card${graceEnd ? ` on ${graceEnd}` : ' soon'}. You have full access until then.`
                    : `Please contact your team admin to update billing${graceEnd ? ` before ${graceEnd}` : ''}. You have full access until then.`}
            </Typography>
            {canResubscribe && (
                <Button
                    size="small" variant="contained" onClick={onManage}
                    sx={{ fontFamily, textTransform: 'none', bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}
                >
                    Manage Billing
                </Button>
            )}
        </Box>
    );
}

// Inner layout component that uses PanelContext
function LayoutContent() {
    const navigate = useNavigate();
    const theme = useTheme();
    const [userData, setUserData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isMobileView, setIsMobileView] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [graceBanner, setGraceBanner] = useState(null);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Get panel context
    const { activePanel, isLoading: panelLoading } = usePanel();

    useEffect(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                const parsedUser = JSON.parse(userStr);
                setUserData(parsedUser);
            } catch (error) {
                console.error('Error parsing user data:', error);
                localStorage.removeItem('user');
                navigate('/Login');
            }
        } else {
            navigate('/Login');
        }
        setIsLoading(false);
    }, [navigate]);

    useEffect(() => {
        if (!userData || isLoading) return;

        const checkAuthAndSubscription = async () => {
            try {
                const [subResult, accessResult] = await Promise.allSettled([
                    axiosInstance.get(`/subscriptions/current`),
                    axiosInstance.get(`/subscriptions/access-status`),
                ]);

                // --- current subscription (refreshes the cached subscription object) ---
                let fetchedSubscription = null;
                if (subResult.status === 'fulfilled' && subResult.value?.data) {
                    fetchedSubscription = subResult.value.data.data || subResult.value.data;
                    if (!fetchedSubscription || !fetchedSubscription.plan) {
                        if (fetchedSubscription?.status !== 'freemium') {
                            fetchedSubscription = null;
                        }
                    }
                } else if (subResult.reason?.response?.status === 404) {
                    const responseData = subResult.reason.response.data;
                    fetchedSubscription = responseData?.data?.plan?.name === 'freemium' ? responseData.data : null;
                }

                // --- billing access state (drives the resubscription gate / grace banner) ---
                let accessData = null;
                if (accessResult.status === 'fulfilled' && accessResult.value?.data) {
                    accessData = accessResult.value.data;
                }
                const accessState = accessData?.state || null;

                // Refresh cached subscription + billing_access in localStorage.
                const userStr = localStorage.getItem('user');
                if (userStr) {
                    const prevUser = JSON.parse(userStr);
                    const updatedUser = {
                        ...prevUser,
                        active_team: {
                            ...prevUser.active_team,
                            subscription: fetchedSubscription,
                            ...(accessData ? { billing_access: accessData } : {}),
                        },
                        teams: (prevUser.teams || []).map(team =>
                            team.team_id === prevUser.active_team.team_id
                                ? { ...team, subscription: fetchedSubscription, ...(accessData ? { billing_access: accessData } : {}) }
                                : team
                        ),
                    };
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                }

                // --- the gate ---
                if (accessState === 'locked') {
                    setGraceBanner(null);
                    // Locked users may still reach /Billing (manage cards / view invoices);
                    // every other route is redirected to the standalone resubscription page.
                    if (!window.location.pathname.toLowerCase().startsWith('/billing')) {
                        navigate('/resubscribe');
                    }
                    return;
                }
                setGraceBanner(accessState === 'grace' ? accessData : null);
                if (accessState === 'no_subscription') {
                    localStorage.removeItem(SETUP_COMPLETE_LOCAL_KEY);
                    navigate('/SignUp-Process-Steps');
                    return;
                }
                // Fallback: if access-status is unavailable, preserve the original behaviour.
                if (accessState === null && !fetchedSubscription) {
                    localStorage.removeItem(SETUP_COMPLETE_LOCAL_KEY);
                    navigate('/SignUp-Process-Steps');
                }
            } catch (err) {
                console.error("Unexpected error during subscription/access check:", err);
            }
        };

        checkAuthAndSubscription();
    }, [userData?.access_token, isLoading, navigate]);

    useEffect(() => {
        setIsMobileView(isMobile);
        setIsSidebarOpen(!isMobile);
    }, [isMobile]);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleMobileSidebarClose = () => {
        if (isMobileView) {
            setIsSidebarOpen(false);
        }
    };

    if (!userData) {
        return null;
    }

    return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap');`}</style>
            <Box sx={{
                display: 'flex',
                minHeight: '100vh',
                fontFamily,
                background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #e0e7ff 70%, #f0f4ff 100%)',
                overflow: 'hidden',
            }}>
                <CssBaseline />
                <Header
                    toggleSidebar={toggleSidebar}
                    isSidebarCollapsed={!isSidebarOpen}
                    isMobile={isMobileView}
                />
                {/* Render appropriate sidebar based on active panel */}
                {activePanel === PanelType.FINANCE ? (
                    <FinanceSidebar
                        isOpen={isSidebarOpen}
                        drawerWidth={drawerWidth}
                        onClose={handleMobileSidebarClose}
                        isMobile={isMobileView}
                        toggleSidebar={toggleSidebar}
                    />
                ) : activePanel === PanelType.TRADING ? (
                    <TradingSidebar
                        isOpen={isSidebarOpen}
                        drawerWidth={drawerWidth}
                        onClose={handleMobileSidebarClose}
                        isMobile={isMobileView}
                        toggleSidebar={toggleSidebar}
                    />
                ) : activePanel === PanelType.STUDENT ? (
                    <StudentSidebar
                        isOpen={isSidebarOpen}
                        drawerWidth={drawerWidth}
                        onClose={handleMobileSidebarClose}
                        isMobile={isMobileView}
                        toggleSidebar={toggleSidebar}
                    />
                ) : activePanel === PanelType.CUSTOMER ? (
                    <CustomerSidebar
                        isOpen={isSidebarOpen}
                        drawerWidth={drawerWidth}
                        onClose={handleMobileSidebarClose}
                        isMobile={isMobileView}
                        toggleSidebar={toggleSidebar}
                    />
                ) : (
                    <CreatorSidebar
                        isOpen={isSidebarOpen}
                        drawerWidth={drawerWidth}
                        onClose={handleMobileSidebarClose}
                        isMobile={isMobileView}
                        toggleSidebar={toggleSidebar}
                    />
                )}
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '100vh',
                        width: {
                            xs: '100%',
                            md: isSidebarOpen
                                ? `calc(100% - ${drawerWidth}px)`
                                : `calc(100% - ${collapsedWidth}px)`
                        },
                        ml: {
                            xs: 0,
                            md: isSidebarOpen ? `${drawerWidth}px` : `${collapsedWidth}px`
                        },
                        transition: 'margin-left 0.3s ease, width 0.3s ease',
                    }}
                >
                    {/* Content Area */}
                    <Box sx={{
                        flexGrow: 1,
                        p: { xs: 1.5, sm: 2, md: 3 },
                        pt: { xs: 8, sm: 9, md: 10 },
                        overflowX: 'hidden',
                    }}>
                        {graceBanner && <GraceBanner info={graceBanner} onManage={() => navigate('/Billing')} />}
                        <Outlet />
                    </Box>

                    {/* Footer */}
                    <Box sx={{ p: 2 }}>
                        <Footer />
                    </Box>
                </Box>
            </Box>
        </>
    );
}

// Wrapper component that provides PanelContext
function Layout() {
    return (
        <PanelProvider>
            <CompanySettingsProvider>
                <FinanceAccountProvider>
                    <LayoutContent />
                </FinanceAccountProvider>
            </CompanySettingsProvider>
        </PanelProvider>
    );
}

export default Layout;
